/*
 * Delivery / pickup date & time slot picker -- interaction logic.
 *
 * Loaded ONCE, sitewide, via a normal <script src> tag in layout/theme.liquid
 * (NOT inlined inside the snippet markup). This is the fix for "only
 * Delivery/Pickup shows, no date/time appears" recurring after any cart
 * change: the picker's HTML (snippets/delivery-pickup-scheduler.liquid) gets
 * reinserted into the page via `element.innerHTML = <fresh markup>` every
 * time the cart drawer re-renders (adding an item, changing quantity -- see
 * assets/cart-drawer.js CartDrawer.renderContents / CartDrawerItems). Setting
 * .innerHTML never executes any <script> tag inside the markup it inserts, so
 * if this logic lived inside that snippet, every reinsertion produced a fresh
 * picker with zero listeners attached.
 *
 * Fix: keep this logic in its own asset file, loaded exactly once per page
 * load from a location that never gets replaced. All interaction is handled
 * through:
 *   1. Delegated `document`-level listeners (change/click) -- these keep
 *      working no matter how many times the surrounding HTML is swapped,
 *      because they're attached to `document`, not to the elements that get
 *      replaced.
 *   2. A subscription to the theme's own PUB_SUB_EVENTS.cartUpdate event
 *      (published by assets/cart.js and assets/product-form.js right after
 *      every add-to-cart / quantity-change re-render completes) to
 *      explicitly re-sync the visual state of any picker instance right
 *      after the DOM settles. This is the theme's own designed
 *      synchronization point, so it's deterministic -- no guessing about
 *      mutation timing.
 *   3. A MutationObserver on <body> as a redundant safety net, in case some
 *      future code path swaps this markup in without publishing cartUpdate.
 */
(function () {
  // ---- Business rules (mirrors the schedule used elsewhere) ----------
  var QATAR_UTC_OFFSET_HOURS = 3;

  var DELIVERY_SLOTS = [
    { id: "delivery-14-17", label: "2:00 PM - 5:00 PM", startHour: 14, startMinute: 0 },
    { id: "delivery-16-19", label: "4:00 PM - 7:00 PM", startHour: 16, startMinute: 0 }
  ];
  var PICKUP_SLOTS = [
    { id: "pickup-14-17", label: "2:00 PM - 5:00 PM", startHour: 14, startMinute: 0 }
  ];

  function getQatarNow() {
    var shifted = new Date(Date.now() + QATAR_UTC_OFFSET_HOURS * 60 * 60 * 1000);
    var y = shifted.getUTCFullYear();
    var m = String(shifted.getUTCMonth() + 1).padStart(2, "0");
    var d = String(shifted.getUTCDate()).padStart(2, "0");
    return {
      dateStr: y + "-" + m + "-" + d,
      minutesSinceMidnight: shifted.getUTCHours() * 60 + shifted.getUTCMinutes()
    };
  }

  function isSlotAvailable(dateStr, slot, now) {
    if (dateStr < now.dateStr) return false;
    if (dateStr > now.dateStr) return true;
    var slotStart = slot.startHour * 60 + slot.startMinute;
    return slotStart > now.minutesSinceMidnight;
  }

  function slotsFor(kind, dateStr, now) {
    var source = kind === "delivery" ? DELIVERY_SLOTS : PICKUP_SLOTS;
    return source.filter(function (s) { return isSlotAvailable(dateStr, s, now); });
  }

  // ---- Helpers that always look up the CURRENT elements for a given root,
  // rather than caching references that could go stale after an innerHTML
  // swap replaces the nodes. -----------------------------------------------
  function els(root) {
    return {
      kindSelect: root.querySelector('[data-role="kind"]'),
      dateField: root.querySelector('[data-role="date-field"]'),
      dateSelect: root.querySelector('[data-role="date"]'),
      slotField: root.querySelector('[data-role="slot-field"]'),
      slotSelect: root.querySelector('[data-role="slot"]'),
      warningEl: root.querySelector('[data-role="warning"]'),
      statusEl: root.querySelector('[data-role="status"]')
    };
  }

  function populateDates(root, selected) {
    var e = els(root);
    var now = getQatarNow();
    // No upper bound -- any future date is selectable, only "today or
    // later" is enforced.
    var minDate = now.dateStr;
    e.dateSelect.min = minDate;
    e.dateSelect.removeAttribute("max");
    var inRange = selected && selected >= minDate;
    e.dateSelect.value = inRange ? selected : minDate;
  }

  function populateSlots(root, kind, dateStr, selectedLabel) {
    var e = els(root);
    var now = getQatarNow();
    var slots = slotsFor(kind, dateStr, now);
    e.slotSelect.innerHTML = "";
    var placeholder = document.createElement("option");
    placeholder.value = "";
    placeholder.textContent = "Select a time slot";
    e.slotSelect.appendChild(placeholder);
    slots.forEach(function (s) {
      var opt = document.createElement("option");
      opt.value = s.id;
      opt.textContent = s.label;
      e.slotSelect.appendChild(opt);
    });

    e.warningEl.hidden = slots.length > 0;
    if (slots.length === 0) {
      e.warningEl.textContent = "All of today's time slots have already started. Please choose a later date.";
    }

    if (selectedLabel) {
      var match = slots.filter(function (s) { return s.label === selectedLabel; })[0];
      if (match) e.slotSelect.value = match.id;
    }
  }

  function updateCartAttributes(root) {
    var e = els(root);
    var kind = e.kindSelect.value;
    var date = e.dateSelect.value;
    var slotId = e.slotSelect.value;
    var slotObj = null;
    if (slotId) {
      var source = kind === "delivery" ? DELIVERY_SLOTS : PICKUP_SLOTS;
      slotObj = source.filter(function (s) { return s.id === slotId; })[0] || null;
    }

    var attributes = {
      "Fulfillment schedule type": kind === "delivery" ? "Delivery" : kind === "pickup" ? "Pickup" : "",
      "Requested date": kind ? date : "",
      "Requested time slot": slotObj ? slotObj.label : ""
    };

    e.statusEl.hidden = false;
    e.statusEl.textContent = "Saving your selection...";

    fetch(window.Shopify && window.Shopify.routes && window.Shopify.routes.root ? window.Shopify.routes.root + "cart/update.js" : "/cart/update.js", {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify({ attributes: attributes })
    })
      .then(function (res) { return res.json(); })
      .then(function () {
        // Keep the "last saved" state on the root element itself (not just in
        // JS variables) so the checkout guard -- which re-reads this attribute
        // fresh on every click -- stays correct even if the surrounding markup
        // gets replaced later. Apply it to EVERY picker instance currently in
        // the DOM (not just this root), so the /cart page footer and the cart
        // drawer never disagree about what was last saved.
        document.querySelectorAll(".dps-root").forEach(function (otherRoot) {
          otherRoot.setAttribute("data-saved-kind", attributes["Fulfillment schedule type"]);
          otherRoot.setAttribute("data-saved-date", attributes["Requested date"]);
          otherRoot.setAttribute("data-saved-slot", attributes["Requested time slot"]);
        });

        if (kind && slotObj) {
          e.statusEl.textContent = "Saved: " + attributes["Fulfillment schedule type"] + " on " + date + ", " + slotObj.label + ".";
        } else if (kind) {
          e.statusEl.textContent = "Please also choose a time slot before checking out.";
        } else {
          e.statusEl.hidden = true;
        }
      })
      .catch(function () {
        e.statusEl.textContent = "Could not save your selection -- please try again.";
      });
  }

  // ---- Restore visual state (show date/time fields + previous selection)
  // for a specific root element. Called for every root that appears in the
  // DOM, whether it was there at initial page load or got reinjected later
  // by a section refresh (add-to-cart, quantity change). Always re-applies
  // (no longer gated behind a "run once per root" flag) so that a root whose
  // saved-* attributes changed since the last time it was scanned -- e.g. the
  // /cart page footer just saved a selection and the cart drawer's fields
  // haven't reflected it yet -- gets updated correctly.
  function initRootVisualState(root) {
    if (!root) return;

    var e = els(root);
    var savedKind = root.getAttribute("data-saved-kind");
    var savedDate = root.getAttribute("data-saved-date");
    var savedSlot = root.getAttribute("data-saved-slot");
    var initialKind = savedKind === "Delivery" ? "delivery" : savedKind === "Pickup" ? "pickup" : "";

    populateDates(root, savedDate);
    if (initialKind) {
      e.kindSelect.value = initialKind;
      e.dateField.hidden = false;
      e.slotField.hidden = false;
      populateSlots(root, initialKind, e.dateSelect.value, savedSlot);
    } else {
      e.kindSelect.value = "";
      e.dateField.hidden = true;
      e.slotField.hidden = true;
    }
  }

  function scanForRoots(container) {
    if (!container) return;
    if (container.nodeType !== 1) return;
    if (container.classList && container.classList.contains("dps-root")) {
      initRootVisualState(container);
    }
    if (container.querySelectorAll) {
      container.querySelectorAll(".dps-root").forEach(initRootVisualState);
    }
  }

  // Initial pass, for whatever is already in the DOM at parse time.
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", function () { scanForRoots(document.body); });
  } else {
    scanForRoots(document.body);
  }

  // Primary sync point: the theme's own cart-update pub/sub event, published
  // right after add-to-cart and quantity-change re-renders finish replacing
  // the cart drawer / cart page footer markup.
  if (typeof subscribe === "function" && typeof PUB_SUB_EVENTS === "object") {
    subscribe(PUB_SUB_EVENTS.cartUpdate, function () {
      scanForRoots(document.body);
    });
  }

  // Redundant safety net: catch any other code path that reinjects this
  // markup without publishing cartUpdate.
  var observer = new MutationObserver(function (mutations) {
    mutations.forEach(function (m) {
      m.addedNodes.forEach(scanForRoots);
    });
  });
  observer.observe(document.body, { childList: true, subtree: true });

  // ---- Delegated interaction handlers ------------------------------------
  // Bound once, at the document level, so they keep working no matter how
  // many times the picker's markup gets swapped out from under them.
  document.addEventListener(
    "change",
    function (event) {
      var kindEl = event.target.closest && event.target.closest('[data-role="kind"]');
      if (kindEl) {
        var kindRoot = kindEl.closest(".dps-root");
        if (!kindRoot) return;
        var e = els(kindRoot);
        var kind = e.kindSelect.value;
        if (!kind) {
          e.dateField.hidden = true;
          e.slotField.hidden = true;
          updateCartAttributes(kindRoot);
          return;
        }
        e.dateField.hidden = false;
        e.slotField.hidden = false;
        populateDates(kindRoot, e.dateSelect.value);
        populateSlots(kindRoot, kind, e.dateSelect.value, null);
        updateCartAttributes(kindRoot);
        return;
      }

      var dateEl = event.target.closest && event.target.closest('[data-role="date"]');
      if (dateEl) {
        var dateRoot = dateEl.closest(".dps-root");
        if (!dateRoot) return;
        var e2 = els(dateRoot);
        // Some browsers allow typing a date outside min via keyboard even
        // though the calendar UI disables it -- clamp defensively.
        if (e2.dateSelect.value < e2.dateSelect.min) e2.dateSelect.value = e2.dateSelect.min;
        populateSlots(dateRoot, e2.kindSelect.value, e2.dateSelect.value, null);
        updateCartAttributes(dateRoot);
        return;
      }

      var slotEl = event.target.closest && event.target.closest('[data-role="slot"]');
      if (slotEl) {
        var slotRoot = slotEl.closest(".dps-root");
        if (!slotRoot) return;
        updateCartAttributes(slotRoot);
        return;
      }
    },
    true
  );

  // ---- Block checkout until a valid slot is chosen -----------------------
  // Delegated at the document level and matched by the checkout button's own
  // id (each picker instance records which checkout button it guards via
  // data-checkout-button-id), so this survives both the button and the
  // picker being replaced by a section refresh.
  document.addEventListener(
    "click",
    function (event) {
      var roots = document.querySelectorAll(".dps-root");
      for (var i = 0; i < roots.length; i++) {
        var candidateRoot = roots[i];
        var btnId = candidateRoot.getAttribute("data-checkout-button-id");
        if (!btnId) continue;
        var target = event.target && event.target.closest ? event.target.closest("#" + btnId) : null;
        if (!target) continue;

        var currentSavedKind = candidateRoot.getAttribute("data-saved-kind");
        var currentSavedSlot = candidateRoot.getAttribute("data-saved-slot");
        if (!currentSavedKind || !currentSavedSlot) {
          event.preventDefault();
          var currentWarning = candidateRoot.querySelector('[data-role="warning"]');
          if (currentWarning) {
            currentWarning.hidden = false;
            currentWarning.textContent = "Please choose a delivery/pickup date and time slot before checking out.";
          }
          candidateRoot.scrollIntoView({ behavior: "smooth", block: "center" });
        }
        return;
      }
    },
    true
  );
})();
